import { Box, Stack } from '@mui/material';
import { logger } from '../../services/logger/logger';
import { ColDef, ISetFilterParams, SetFilterValuesFuncParams } from 'ag-grid-community';
import { debounce, isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Chips from '../../components/common/Chips';
import CustomActionButton from '../../components/common/customAgGrid/CustomActionButton';
import CustomAgDateFilter from '../../components/common/customAgGrid/CustomAgDateFilter';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgTextFilter from '../../components/common/customAgGrid/CustomAgTextFilter';
import ServerSideAgGrid from '../../components/common/customAgGrid/ServerSideAgGrid';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import PageHeader from '../../components/PageHeader';
import SelectTypeModal from '../../components/SelectTypeModal';
import {
  useDeleteRobMutation,
  useGetManagerFilterOptionsQuery,
  useGetRobsListMutation,
  useGetTypeFilterOptionsQuery,
  useGetVesselFilterOptionsQuery,
} from '../../store/apiSlices/robs/robsApis';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { canEditRobAndDeliveries } from '../../utils/permissions';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { dateRangeFilterPredicate } from '../../utils/agGridPredicates';
import { TruncatedCellText } from '../../components/common/styled/TruncatedCellText';
import { TableCardContent } from '../../components/common/styled/TableCardContent';
import { useTranslation } from 'react-i18next';

// Robs types data
const ROBS_TYPES = [
  { value: 'standard', label: 'robs.standardLabel' },
  { value: 'endOfManagement', label: 'robs.endOfManagementLabel' },
  { value: 'newBuilding', label: 'robs.newBuildingLabel' },
];

// ------------------- Types -------------------
interface Rob {
  id: string | number;
  srno: number;
  vessel: string;
  vesselId?: number;
  manager: string;
  port: string;
  date: string;
  type: string;
  remarks: string;
}

type FilterValues = Record<string, string | number | string[] | number[] | any | null | undefined>;

interface ActionItem {
  label: string;
  variant: 'blue' | 'outlinedBlue' | 'red' | 'gray';
  onClick: () => void;
}

interface BreadcrumbOption {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

interface TableMenuOption {
  label: string;
  onClick: (_rowData?: any) => void;
}

// ------------------- Component -------------------
const STORAGE_KEY = 'robs_grid_state';

const getSavedGridState = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (error) {
    logger.error('Error retrieving saved grid state:', error as Error);
  }
  return null;
};

const RobsContainer: React.FC = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canAddorEdit = canEditRobAndDeliveries(userInfo?.authorityName);
  const navigate = useNavigate();

  // Restore saved state from sessionStorage
  const savedState = useRef(getSavedGridState());

  const [filterValues, setFilterValues] = useState<FilterValues>(
    savedState.current?.filterValues || {},
  );
  const [debouncedFilterValues, setDebouncedFilterValues] = useState<FilterValues>(
    savedState.current?.filterValues || {},
  );
  const [showRobsTypeModal, setShowRobsTypeModal] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<string>('');
  const [typeSelectionError, setTypeSelectionError] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState<number>(savedState.current?.pageSize || 25);
  const [pageNumber, setPageNumber] = useState<number>(savedState.current?.pageNumber || 0);
  const [sortBy, setSortBy] = useState<any>(savedState.current?.sortBy || {});
  const [tableColumns, setTableColumns] = useState<ColDef[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<Rob | null>(null);
  const [resetFiltersSignal, setResetFiltersSignal] = useState<number>(0);
  const [isFilterActive, setIsFilterActive] = useState(savedState.current?.isFilterActive || false);
  const [gridFilterModel, setGridFilterModel] = useState<any>(
    savedState.current?.gridFilterModel || null,
  );

  // Flag to skip the first onFilterChange triggered by grid restoring saved filters
  const isRestoringRef = useRef(!!savedState.current?.gridFilterModel);

  // Persist grid state to sessionStorage whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          filterValues,
          pageNumber,
          pageSize,
          sortBy,
          isFilterActive,
          gridFilterModel,
        }),
      );
    } catch (error) {
      logger.error('Error saving grid state:', error as Error);
    }
  }, [filterValues, pageNumber, pageSize, sortBy, isFilterActive, gridFilterModel]);

  // Mock Api call
  const [getRobsList, { data: robsList, isLoading, isError }] = useGetRobsListMutation();
  const [deleteRob, { isLoading: isDeleting }] = useDeleteRobMutation();

  const { data: vesselFilterOptions } = useGetVesselFilterOptionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: managerFilterOptions } = useGetManagerFilterOptionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: typeFilterOptions } = useGetTypeFilterOptionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  // Debounced filter values for API calls
  const debouncedSetFilterValues = useMemo(
    () =>
      debounce((newFilterValues: FilterValues) => {
        setDebouncedFilterValues((prev) =>
          isEqual(prev, newFilterValues) ? prev : newFilterValues,
        );
      }, 1000),
    [],
  );

  // Create API parameters object
  const apiParams = useMemo(
    () => ({
      ...debouncedFilterValues,
      page: pageNumber,
      size: pageSize,
      ...(Object.keys(sortBy).length > 0 && sortBy),
    }),
    [debouncedFilterValues, pageNumber, pageSize, sortBy],
  );

  // Single API call effect - handles both initial load and parameter changes
  const prevApiParamsRef = useRef<any>(null);
  useEffect(() => {
    if (!apiParams) return;
    if (prevApiParamsRef.current && isEqual(prevApiParamsRef.current, apiParams)) {
      return;
    }
    prevApiParamsRef.current = apiParams;
    getRobsList(apiParams);
  }, [apiParams, getRobsList]);

  // Update debounced filter values when filterValues change
  useEffect(() => {
    debouncedSetFilterValues(filterValues);
  }, [filterValues, debouncedSetFilterValues]);

  // Page header action buttons
  const actionBtns: ActionItem[] = canAddorEdit
    ? [
        {
          label: t('robs.addRob'),
          variant: 'blue',
          onClick: () => setShowRobsTypeModal(true),
        },
      ]
    : [];

  // Breadcrumb options
  const breadcrumbOptions: BreadcrumbOption[] = [
    { label: t('robs.title'), isActive: true, onClick: () => {} },
  ];

  // Delete handlers
  const handleDeleteClick = useCallback((rowData: Rob) => {
    setDeleteTarget(rowData);
    setShowDeleteConfirm(true);
  }, []);

  // Table row action buttons - function to generate menu options based on row data
  const getTableMenuOptions = useCallback(
    (rowData?: Rob): TableMenuOption[] => {
      const menuOptions: TableMenuOption[] = [
        {
          label: t('buttons.edit'),
          onClick: (rowData?: Rob) => {
            if (rowData) {
              navigate(`/rob/edit/${rowData.id}`, {
                state: {
                  robsData: rowData,
                  isEdit: true,
                },
              });
            }
          },
        },
      ];

      // Only add Delete option if it's NOT a 2nd ACQ ROB (secondHand type)
      // Check for various representations of the secondHand type
      const isSecondHandROB =
        rowData?.type?.toLowerCase().includes('acq') ||
        rowData?.type?.toLowerCase().includes('acquisition') ||
        rowData?.type === 'secondHand' ||
        rowData?.type === 'SECONDHAND' ||
        rowData?.type === 'ACQ';

      if (!isSecondHandROB) {
        menuOptions.push({
          label: t('buttons.delete'),
          onClick: (rowData?: Rob) => {
            if (rowData) {
              handleDeleteClick(rowData);
            }
          },
        });
      }

      return menuOptions;
    },
    [navigate, handleDeleteClick, t],
  );

  const vesselFilterParams: ISetFilterParams = useMemo(
    () => ({
      values: (params: SetFilterValuesFuncParams) => {
        if (Array.isArray(vesselFilterOptions) && vesselFilterOptions.length > 0) {
          return params.success(vesselFilterOptions.map((opt: any) => opt.label));
        } else {
          params.success([]);
        }
      },
    }),
    [vesselFilterOptions],
  );

  const managerFilterParams: ISetFilterParams = useMemo(
    () => ({
      values: (params: SetFilterValuesFuncParams) => {
        if (Array.isArray(managerFilterOptions) && managerFilterOptions.length > 0) {
          return params.success(managerFilterOptions.map((opt: any) => opt.label));
        } else {
          params.success([]);
        }
      },
    }),
    [managerFilterOptions],
  );

  const typeFilterParams: ISetFilterParams = useMemo(
    () => ({
      values: (params: SetFilterValuesFuncParams) => {
        if (Array.isArray(typeFilterOptions) && typeFilterOptions.length > 0) {
          return params.success(typeFilterOptions.map((opt: any) => opt));
        } else {
          params.success([]);
        }
      },
    }),
    [typeFilterOptions],
  );

  // Table columns
  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: t('table.columns.id'),
        sortable: true,
        width: 120,
        maxWidth: 120,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgTextFilter,
      },
      {
        field: 'vessel',
        headerName: t('table.columns.vessel'),
        width: 200,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        filterParams: vesselFilterParams,
      },
      {
        field: 'manager',
        headerName: t('table.columns.manager'),
        width: 200,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        filterParams: managerFilterParams,
      },
      {
        field: 'date',
        headerName: t('table.columns.date'),
        width: 180,
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgDateFilter,
        filterParams: {
          suppressAndOrCondition: true,
          maxNumConditions: 1,
          filterOptions: [
            {
              displayKey: 'inRange',
              displayName: t('common.between'),
              numberOfInputs: 2,
              predicate: dateRangeFilterPredicate,
            },
            'equals',
          ],
          defaultOption: 'inRange',
        },
      },
      {
        field: 'type',
        headerName: t('table.columns.type'),
        width: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        filterParams: typeFilterParams,
        cellRenderer: (params: any) => (
          <Chips width="150px" label={params?.data?.type} capitalize={false} />
        ),
      },
      {
        field: 'remarks',
        headerName: t('table.columns.remarks'),
        width: 300,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgTextFilter,
        cellRenderer: (params: any) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <TruncatedCellText>{params?.value}</TruncatedCellText>
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        maxWidth: 50,
        flex: 0,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params: any) => (
          <CustomActionButton
            menuOptions={getTableMenuOptions(params.data)}
            rowData={params.data}
          />
        ),
      },
    ],
    [managerFilterParams, vesselFilterParams, typeFilterParams, getTableMenuOptions, t],
  );

  useEffect(() => {
    setTableColumns(canAddorEdit ? columns : columns.filter((col) => col.field !== 'actions'));
  }, [canAddorEdit, columns]);

  // Generate add button label based on selected type
  const getAddButtonLabel = useCallback(
    (type: string): string => {
      if (!type) return t('robs.genericAddRob');
      const robsType = ROBS_TYPES.find((t) => t.value === type);
      if (!robsType) return t('robs.genericAddRob');
      return t('robs.dynamicAddRob', { type: t(robsType.label) });
    },
    [t],
  );

  const handleTypeSelection = useCallback((type: string): void => {
    setSelectedType(type);
    setTypeSelectionError(null);
  }, []);

  const handleTypeConfirm = useCallback((): void => {
    setShowRobsTypeModal(false);
    navigate('/rob/add', { state: { robsType: selectedType } });
  }, [navigate, selectedType]);

  const handleCloseModal = useCallback(() => {
    setShowRobsTypeModal(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;

    try {
      const robId = Number(deleteTarget.id) || 0;
      const vesselId = deleteTarget.vesselId || 0;

      const response = await deleteRob({
        id: robId,
        vesselId,
      }).unwrap();

      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      showSuccessToast(response?.message || t('robs.deleteSuccess'));

      // Refresh the list
      getRobsList(apiParams);
    } catch (error: any) {
      logger.error('Failed to delete ROB', error as Error);
    }
  }, [deleteTarget, deleteRob, getRobsList, apiParams, t]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPageNumber(0); // Reset to first page when page size changes
  }, []);

  const handlePageChange = useCallback((newPageNumber: number) => {
    setPageNumber(newPageNumber - 1); // Convert 1-based to 0-based for API
  }, []);

  const onFilterChange = (filterModel: any) => {
    // Skip processing when the grid is restoring saved filters on mount.
    // The saved filterValues are already correctly initialized from sessionStorage,
    // and rebuilding them here would fail because filter option data hasn't loaded yet.
    if (isRestoringRef.current) {
      isRestoringRef.current = false;
      return;
    }

    const hasFilters = !!(filterModel && Object.keys(filterModel).length > 0);

    // Save the raw AG Grid filter model for restoration
    setGridFilterModel(hasFilters ? filterModel : null);

    // If all filters are cleared from the grid UI
    if (!hasFilters) {
      setIsFilterActive(false);
      setFilterValues({});
      setPageNumber(0);
      return;
    }

    setIsFilterActive(true);

    // Build a clean filter object without mutating the input model
    const builtFilters: FilterValues = {};
    if (filterModel?.id) {
      (builtFilters as any)['ids'] = Number((filterModel as any)['id']?.filter) || null;
    }
    if (filterModel?.vessel) {
      const selectedLabels = (filterModel?.vessel as any)?.values || [];
      const vesselIds = vesselFilterOptions
        ?.filter((vessel: any) => selectedLabels?.includes(vessel.label))
        ?.map((vessel: any) => vessel.value);
      (builtFilters as any)['vesselIds'] = vesselIds && vesselIds.length > 0 ? vesselIds : null;
    }

    if (filterModel?.manager) {
      const selectedLabels = (filterModel?.manager as any)?.values || [];
      const managerIds = managerFilterOptions
        ?.filter((manager: any) => selectedLabels?.includes(manager.label))
        ?.map((manager: any) => manager.value);
      (builtFilters as any)['managerIds'] = managerIds && managerIds.length > 0 ? managerIds : null;
    }

    if (filterModel?.type) {
      const selectedLabels = (filterModel?.type as any)?.values || [];
      const typeIds = typeFilterOptions
        ?.filter((type: any) => selectedLabels?.includes(type))
        ?.map((type: any) => type);
      (builtFilters as any)['types'] =
        typeIds && typeIds.length > 0
          ? typeIds?.map((itm) => {
              if (itm?.toUpperCase() === 'STANDARD') return 'STD';
              if (itm?.toUpperCase() === 'ACQUISITION') return 'ACQ';
              if (itm?.toUpperCase() === 'NEW BUILDING') return 'NEW';
              else return itm;
            })
          : null;
    }

    if (filterModel?.remarks) {
      (builtFilters as any)['comments'] = (filterModel as any)['remarks']?.filter || null;
    }

    if (filterModel?.date != null) {
      if (filterModel.date?.type === 'inRange') {
        const startDate = dayjs((filterModel as any)['date']?.dateFrom).format('YYYY-MM-DD');
        const endDate = dayjs((filterModel as any)['date']?.dateTo).format('YYYY-MM-DD');
        (builtFilters as any)['date.dateFrom'] = startDate;
        (builtFilters as any)['date.dateTo'] = endDate;
        (builtFilters as any)['date.type'] = 'BETWEEN';
      } else if (filterModel.date?.type === 'equals') {
        const date = dayjs((filterModel as any)['date']?.dateFrom).format('YYYY-MM-DD');
        (builtFilters as any)['date.dateFrom'] = date ? date : null;
        (builtFilters as any)['date.type'] = 'EQUALS';
      }
    }

    setFilterValues(builtFilters);
    setPageNumber(0);
  };

  const onSortChange = (sortModel: any) => {
    if (sortModel && sortModel.length > 0) {
      const sort = sortModel[0];
      let colId = sort.colId;
      if (colId === 'vessel') colId = 'vesselName';
      if (colId === 'manager') colId = 'managerName';
      if (colId === 'date') colId = 'eventDate';
      if (colId === 'type') colId = 'type';
      if (colId === 'remarks') colId = 'comments';
      setSortBy({
        [`sortBy[0].sortBy`]: colId,
        [`sortBy[0].direction`]: sort.sort,
      });
      setPageNumber(0);
    } else {
      setSortBy({});
      setPageNumber(0);
    }
  };

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        actions={actionBtns}
        options={breadcrumbOptions}
        resetFilters={() => {
          setIsFilterActive(false);
          setResetFiltersSignal((prev) => (typeof prev === 'number' ? prev + 1 : 1));
          setFilterValues({});
          setPageNumber(0);
          setSortBy({});
          setGridFilterModel(null);
          sessionStorage.removeItem(STORAGE_KEY);
        }}
        isFilterActive={isFilterActive}
      />
      <TableCardContent>
        <ServerSideAgGrid
          columnDefs={tableColumns as any}
          rowData={robsList?.data || []}
          totalRecords={robsList?.pagination?.totalElements || 0}
          isLoading={isLoading}
          isError={isError}
          currentPage={pageNumber + 1}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onFilterChange={onFilterChange}
          onSortChange={onSortChange}
          resetFiltersSignal={resetFiltersSignal}
          initialFilterModel={savedState.current?.gridFilterModel}
        />
      </TableCardContent>

      {showRobsTypeModal && (
        <SelectTypeModal
          title={t('robs.selectType')}
          typesLabel={t('robs.robTypes')}
          open={showRobsTypeModal}
          onClose={handleCloseModal}
          selectedType={selectedType}
          onChangeType={handleTypeSelection}
          onConfirm={handleTypeConfirm}
          onCancel={handleCloseModal}
          isSubmitting={false}
          typeOptions={ROBS_TYPES.map((type) => ({
            ...type,
            label: t(type.label),
          }))}
          confirmButtonLabel={getAddButtonLabel(selectedType)}
          error={typeSelectionError}
        />
      )}

      {showDeleteConfirm && (
        <DeleteConfirmModal
          open={showDeleteConfirm}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          isSubmitting={isDeleting}
          title={t('robs.deleteRob')}
          description={t('common.deleteConfirmation')}
        />
      )}
    </Stack>
  );
};

export default RobsContainer;
